import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { formatINR } from "@/data/content";
import { openWhatsApp } from "@/lib/api";
import { SectionEyebrow } from "@/components/Marquee";

export default function Calculator() {
  const [price, setPrice] = useState(1500000);
  const [segment, setSegment] = useState("sedan"); // hatchback | compact_suv | sedan | fullsize_suv | premium

  const rate = useMemo(() => {
    if (segment === "premium") return 0.075;
    if (segment === "fullsize_suv") return 0.06;
    if (segment === "sedan") return 0.05;
    if (segment === "compact_suv") return 0.045;
    return 0.035; // hatchback
  }, [segment]);

  const segmentLabel = useMemo(() => {
    const labels = {
      hatchback: "Hatchback",
      compact_suv: "Compact SUV",
      sedan: "Sedan",
      fullsize_suv: "Full Size SUV",
      premium: "Premium Segment",
    };
    return labels[segment] || segment;
  }, [segment]);

  const savings = Math.round(price * rate);
  const low = Math.round(savings * 0.8);
  const high = Math.round(savings * 1.2);

  return (
    <section id="calculator" className="relative py-20 md:py-28 bg-[#0A0A0F]" data-testid="calculator-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <SectionEyebrow n="Ch. VII" testId="calculator-eyebrow">Savings Calculator</SectionEyebrow>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.8, 0, 0.2, 1] }}
          className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl"
        >
          How much negotiating room could be <span className="italic gradient-text">hiding in your quote?</span>
        </motion.h2>

        <div className="mt-16 grid lg:grid-cols-5 gap-8 items-stretch">
          <div className="lg:col-span-3 rounded-3xl border border-white/5 bg-surface p-8 md:p-10">
            <label className="block eyebrow gradient-text">Quoted on-road price</label>
            <div className="mt-3 font-serif text-5xl md:text-6xl text-white" data-testid="calc-price-value">
              {formatINR(price)}
            </div>
            <div className="mt-8">
              <Slider
                min={500000}
                max={7500000}
                step={50000}
                value={[price]}
                onValueChange={(v) => setPrice(v[0])}
                data-testid="calc-price-slider"
              />
              <div className="mt-3 flex justify-between text-xs text-silver/45">
                <span>{formatINR(500000)}</span>
                <span>{formatINR(7500000)}</span>
              </div>
            </div>

            <div className="mt-10">
              <label className="block eyebrow gradient-text">Segment</label>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
                {[
                  { id: "hatchback", label: "Hatchback" },
                  { id: "compact_suv", label: "Compact SUV" },
                  { id: "sedan", label: "Sedan" },
                  { id: "fullsize_suv", label: "Full Size SUV" },
                  { id: "premium", label: "Premium Segment" },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSegment(s.id)}
                    className={`py-3 px-2 rounded-full text-xs md:text-sm border transition-colors ${
                      segment === s.id
                        ? "gradient-bg text-white border-transparent"
                        : "border-white/10 text-silver/80 hover:bg-white/5"
                    }`}
                    data-testid={`calc-segment-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 relative rounded-3xl gradient-border bg-[#141323] p-8 md:p-10 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl gradient-bg pointer-events-none" />
            <div className="relative">
              <p className="eyebrow gradient-text">Estimated negotiation opportunity</p>
              <div className="mt-4 font-serif text-white leading-none" data-testid="calc-savings-value">
                <span className="text-[9vw] md:text-[3.6rem]">{formatINR(low)}</span>
                <span className="text-2xl md:text-4xl text-silver/50 mx-1">–</span>
                <span className="text-[9vw] md:text-[3.6rem]">{formatINR(high)}</span>
              </div>
              <p className="mt-4 text-silver/70 text-sm leading-relaxed">
                Based on our current buyer-side average of {(rate * 100).toFixed(1)}% off on-road price for the {segmentLabel} segment.
              </p>
              <p className="mt-3 text-[11px] text-silver/45 leading-relaxed">
                This is an estimate, not a guaranteed saving. Actual savings depend on the vehicle, dealer, inventory, offers, timing and market conditions.
              </p>
              <button
                onClick={() => openWhatsApp({ source: "calculator", context: `price:${price} segment:${segment}` })}
                className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium text-white gradient-bg hover:scale-[1.03] transition-transform"
                data-testid="calc-lock-cta"
              >
                Get My Deal Negotiated
                <span className="w-2 h-2 rounded-full bg-white/90 wa-pulse" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-surface p-6 md:p-7" data-testid="calc-methodology">
          <p className="eyebrow gradient-text">How we calculate your savings</p>
          <p className="mt-3 text-sm text-silver/70 leading-relaxed">
            <span className="text-white">Benchmark price</span> (the market rate for your car, city and segment) minus{" "}
            <span className="text-white">final verified purchase price</span> equals{" "}
            <span className="text-white">verified savings</span> — the number our 20% performance fee is calculated on.
          </p>
        </div>
      </div>
    </section>
  );
}
